import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type Node = { id: number; x: number; y: number; label: string };
type Edge = { a: number; b: number };
type Tool = "move" | "node" | "edge" | "delete";

const starterNodes: Node[] = [
  { id: 1, x: 0.18, y: 0.27, label: "A" },
  { id: 2, x: 0.43, y: 0.16, label: "B" },
  { id: 3, x: 0.73, y: 0.25, label: "C" },
  { id: 4, x: 0.81, y: 0.68, label: "D" },
  { id: 5, x: 0.48, y: 0.79, label: "E" },
  { id: 6, x: 0.17, y: 0.68, label: "F" },
];

const starterEdges: Edge[] = [
  { a: 1, b: 2 }, { a: 2, b: 3 }, { a: 3, b: 4 }, { a: 4, b: 5 },
  { a: 5, b: 6 }, { a: 6, b: 1 }, { a: 2, b: 5 }, { a: 2, b: 6 },
];

function componentCount(nodes: Node[], edges: Edge[]) {
  if (!nodes.length) return 0;
  const seen = new Set<number>();
  let count = 0;
  for (const node of nodes) {
    if (seen.has(node.id)) continue;
    count += 1;
    const queue = [node.id];
    seen.add(node.id);
    while (queue.length) {
      const current = queue.shift()!;
      for (const edge of edges) {
        const next = edge.a === current ? edge.b : edge.b === current ? edge.a : null;
        if (next !== null && !seen.has(next)) {
          seen.add(next);
          queue.push(next);
        }
      }
    }
  }
  return count;
}

export function GraphLab() {
  const [nodes, setNodes] = useState<Node[]>(starterNodes);
  const [edges, setEdges] = useState<Edge[]>(starterEdges);
  const [tool, setTool] = useState<Tool>("move");
  const [selected, setSelected] = useState<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const degrees = useMemo(() => Object.fromEntries(nodes.map((node) => [
    node.id,
    edges.filter((edge) => edge.a === node.id || edge.b === node.id).length,
  ])), [nodes, edges]);
  const components = useMemo(() => componentCount(nodes, edges), [nodes, edges]);
  const odd = useMemo(() => nodes.filter((node) => degrees[node.id] % 2 === 1), [nodes, degrees]);
  const euler = components === 1 && odd.length === 0
    ? "Eulerian circuit"
    : components === 1 && odd.length === 2
      ? "Eulerian trail"
      : "No Eulerian trail";

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const rect = wrap.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    const css = getComputedStyle(document.documentElement);
    const ink = css.getPropertyValue("--ink").trim() || "#11233f";
    const paper = css.getPropertyValue("--surface").trim() || "#fff";
    const muted = css.getPropertyValue("--muted").trim() || "#6f7480";
    const accent = css.getPropertyValue("--accent").trim() || "#e76f51";
    ctx.clearRect(0, 0, rect.width, rect.height);
    const point = (node: Node) => ({ x: node.x * rect.width, y: node.y * rect.height });

    ctx.lineCap = "round";
    for (const edge of edges) {
      const a = nodes.find((node) => node.id === edge.a);
      const b = nodes.find((node) => node.id === edge.b);
      if (!a || !b) continue;
      const p1 = point(a);
      const p2 = point(b);
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.strokeStyle = muted;
      ctx.globalAlpha = 0.55;
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    for (const node of nodes) {
      const p = point(node);
      ctx.beginPath();
      ctx.arc(p.x, p.y, 21, 0, Math.PI * 2);
      ctx.fillStyle = selected === node.id ? accent : paper;
      ctx.fill();
      ctx.strokeStyle = selected === node.id ? accent : ink;
      ctx.lineWidth = selected === node.id ? 4 : 2;
      ctx.stroke();
      ctx.fillStyle = selected === node.id ? "#fffaf3" : ink;
      ctx.font = "600 14px ui-sans-serif, system-ui";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(node.label, p.x, p.y);
    }
  }, [nodes, edges, selected]);

  useEffect(() => {
    draw();
    const observer = new ResizeObserver(draw);
    if (wrapRef.current) observer.observe(wrapRef.current);
    const themeObserver = new MutationObserver(draw);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => {
      observer.disconnect();
      themeObserver.disconnect();
    };
  }, [draw]);

  function position(event: React.PointerEvent<HTMLCanvasElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: (event.clientX - rect.left) / rect.width, y: (event.clientY - rect.top) / rect.height, w: rect.width, h: rect.height };
  }

  function hitNode(x: number, y: number, w: number, h: number) {
    return nodes.find((node) => Math.hypot((node.x - x) * w, (node.y - y) * h) < 28);
  }

  function pointerDown(event: React.PointerEvent<HTMLCanvasElement>) {
    const p = position(event);
    const hit = hitNode(p.x, p.y, p.w, p.h);
    if (tool === "node" && !hit) {
      const nextId = Math.max(0, ...nodes.map((node) => node.id)) + 1;
      setNodes((current) => [...current, { id: nextId, x: p.x, y: p.y, label: String.fromCharCode(64 + ((nextId - 1) % 26) + 1) }]);
    } else if (tool === "edge" && hit) {
      if (selected === null) setSelected(hit.id);
      else if (selected !== hit.id) {
        const exists = edges.some((edge) => (edge.a === selected && edge.b === hit.id) || (edge.a === hit.id && edge.b === selected));
        if (!exists) setEdges((current) => [...current, { a: selected, b: hit.id }]);
        setSelected(null);
      }
    } else if (tool === "delete" && hit) {
      setNodes((current) => current.filter((node) => node.id !== hit.id));
      setEdges((current) => current.filter((edge) => edge.a !== hit.id && edge.b !== hit.id));
      setSelected(null);
    } else if (tool === "move" && hit) {
      event.currentTarget.setPointerCapture(event.pointerId);
      setDragging(hit.id);
      setSelected(hit.id);
    }
  }

  function pointerMove(event: React.PointerEvent<HTMLCanvasElement>) {
    if (dragging === null) return;
    const p = position(event);
    setNodes((current) => current.map((node) => node.id === dragging
      ? { ...node, x: Math.max(0.06, Math.min(0.94, p.x)), y: Math.max(0.08, Math.min(0.92, p.y)) }
      : node));
  }

  function reset() {
    setNodes(starterNodes);
    setEdges(starterEdges);
    setSelected(null);
    setTool("move");
  }

  return (
    <div className="lab-layout">
      <section className="lab-workbench card">
        <div className="section-heading compact">
          <div>
            <span className="eyebrow">Interactive laboratory</span>
            <h2>Build a graph. Watch its properties change.</h2>
          </div>
          <button className="text-button" onClick={reset}>Reset example</button>
        </div>
        <div className="tool-row" aria-label="Graph editing tools">
          {([
            ["move", "Move"],
            ["node", "Add node"],
            ["edge", "Connect"],
            ["delete", "Delete"],
          ] as [Tool, string][]).map(([value, label]) => (
            <button key={value} className={tool === value ? "tool active" : "tool"} onClick={() => { setTool(value); setSelected(null); }} aria-pressed={tool === value}>
              {label}
            </button>
          ))}
          <span className="tool-hint">
            {tool === "move" && "Drag any vertex"}
            {tool === "node" && "Tap empty space"}
            {tool === "edge" && (selected ? "Now choose its neighbour" : "Choose two vertices")}
            {tool === "delete" && "Tap a vertex"}
          </span>
        </div>
        <div className="graph-canvas-wrap" ref={wrapRef}>
          <canvas
            ref={canvasRef}
            onPointerDown={pointerDown}
            onPointerMove={pointerMove}
            onPointerUp={() => setDragging(null)}
            onPointerCancel={() => setDragging(null)}
            aria-label="Interactive undirected graph with draggable vertices"
          />
        </div>
      </section>

      <aside className="lab-analysis">
        <div className="metric-grid">
          <article className="metric card"><span>Vertices</span><strong>{nodes.length}</strong></article>
          <article className="metric card"><span>Edges</span><strong>{edges.length}</strong></article>
          <article className="metric card"><span>Components</span><strong>{components}</strong></article>
          <article className="metric card"><span>Odd degree</span><strong>{odd.length}</strong></article>
        </div>
        <article className="card analysis-card">
          <span className="eyebrow">Euler analysis</span>
          <h3>{euler}</h3>
          <p>{components !== 1
            ? "The graph is disconnected, so no single trail can cover every edge."
            : odd.length === 0
              ? "Every vertex has even degree. Start anywhere and return to the start."
              : odd.length === 2
                ? `Begin at ${odd[0].label} or ${odd[1].label}; finish at the other odd vertex.`
                : `${odd.length} odd-degree vertices prevent an Eulerian trail.`}</p>
        </article>
        <article className="card analysis-card">
          <span className="eyebrow">Degree sequence</span>
          <div className="degree-list">
            {nodes.map((node) => <span key={node.id}><b>{node.label}</b>{degrees[node.id]}</span>)}
          </div>
        </article>
        <article className="card analysis-card matrix-card">
          <span className="eyebrow">Adjacency matrix</span>
          {nodes.length <= 9 ? (
            <div className="matrix" style={{ gridTemplateColumns: `repeat(${nodes.length + 1}, minmax(20px, 1fr))` }}>
              <i />
              {nodes.map((node) => <i key={`h${node.id}`}>{node.label}</i>)}
              {nodes.flatMap((row) => [
                <i key={`r${row.id}`}>{row.label}</i>,
                ...nodes.map((col) => <b key={`${row.id}-${col.id}`}>{edges.some((edge) => (edge.a === row.id && edge.b === col.id) || (edge.a === col.id && edge.b === row.id)) ? 1 : 0}</b>),
              ])}
            </div>
          ) : <p>Matrix hidden above nine vertices to keep it readable.</p>}
        </article>
      </aside>
    </div>
  );
}
